import { FormAutocomplete, FormAutoCompleteProps, TranslationFuncType } from "@itk/mui-components";
import { hasValueAndNotEmpty, OmitSafe } from "@itk/typescript-utils";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Shared_Countries as Countries } from "src/backend/openapi/v1";
import { RootState } from "src/store";
import { countriesActions } from "./countries-store";
import { useCountryTranslation } from "src/translations-enums-hooks/useCountryTranslation";

type FormCountryPhonePrefixAutocompleteOption = {
  prefix: string;
  countryCode: string;
  country: Countries;
  shortName: string;
};

const mapStateToProps = (state: RootState) => ({ ...state.generalReducer.countriesReducer });

const ownActions = {
  getCountriesRequest: countriesActions.getCountriesRequest
};

type OwnProps<TObject extends TranslationFuncType> = OmitSafe<
  FormAutoCompleteProps<FormCountryPhonePrefixAutocompleteOption, number, TObject, keyof TObject>,
  "options" | "getId" | "renderOption" | "getOptionLabel"
>;

type Props<TObject extends TranslationFuncType> = OwnProps<TObject> & ReturnType<typeof mapStateToProps> & typeof ownActions;

const FormCountryPhonePrefixAutocompleteBase = <TObject extends TranslationFuncType>(props: Props<TObject>) => {
  const { formFieldState, formFieldController, countriesLoading, getCountriesRequest, ...rest } = props;
  const { getCountryTranslation, getCountryCode } = useCountryTranslation();

  useEffect(() => {
    if (countriesLoading.loading === "not-started") {
      getCountriesRequest();
    }
  }, [countriesLoading]);

  const options = useMemo((): FormCountryPhonePrefixAutocompleteOption[] => {
    if (countriesLoading.loading !== "has-loaded") return [];
    return [...countriesLoading.data.map(c => ({ prefix: "+" + c.phonePrefix, countryCode: getCountryCode(c.countryId), country: c.countryId, shortName: c.mobileShortName }))];
  }, [getCountryTranslation, countriesLoading]);

  return (
    <FormAutocomplete
      disableClearable
      formFieldState={formFieldState}
      formFieldController={formFieldController}
      options={options}
      {...rest}
      getId={option => option.country}
      getOptionKey={option => `${option.country}-${option.countryCode}-${option.shortName}`}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.country}-${option.countryCode}-${option.shortName}`}>
          <Stack direction="row" gap={1} alignItems="center">
            {hasValueAndNotEmpty(option.countryCode) && (
              <>
                <Box>{option.prefix}</Box>
                <Typography>({option.shortName})</Typography>
              </>
            )}
          </Stack>
        </Box>
      )}
      getOptionLabel={option => option.prefix}
    />
  );
};

export const FormCountryPhonePrefixAutocomplete: <TObject extends TranslationFuncType>(props: OwnProps<TObject>) => JSX.Element = connect(
  mapStateToProps,
  ownActions
)(FormCountryPhonePrefixAutocompleteBase) as <TObject extends TranslationFuncType>(props: OwnProps<TObject>) => JSX.Element;
